import * as amplitude from '@amplitude/analytics-browser'
import * as Sentry from '@sentry/browser'
import fetch from 'cross-fetch'

import { reportError } from 'src/lib/js/clientError'
import {
  isToastLocalSurfaceRequest,
  TOASTTAB_BOO_PATH
} from 'src/public/js/siteUtilities'
import { getGuestAnalyticsClient } from 'src/lib/js/guest-analytics'
import { getSessionManager } from 'src/lib/js/guest-analytics/sessionManager'
import {
  TrackingEvents,
  TRACKER_EVENT_FILTERS
} from 'src/shared/components/common/tracking/TrackingEvents'
import type { Pixels } from 'src/apollo/sites'
import type { AdsManagerRestaurantConversionsData } from 'src/apollo/onlineOrdering'
import type { GuestAnalyticsClient } from 'src/lib/js/guest-analytics'
import type { ConversionData } from 'src/lib/js/guest-analytics/types'

import { amplitudeApiKey, owg, resources } from 'config'

const SESSION_EVENT_PROPERTIES_KEY = 'toast_session_event_properties'

function readSessionEventProperties(): Record<string, any> {
  try {
    return JSON.parse(
      sessionStorage.getItem(SESSION_EVENT_PROPERTIES_KEY) ?? '{}'
    ) as Record<string, any>
  } catch {
    return {}
  }
}

export interface TrackerOptions {
  blacklisted?: boolean
  debug?: boolean
}

/**
 * Determines if a specific tracker should receive a given event.
 * If no filter is defined for a tracker, it receives all events (allow-all default).
 *
 * @param tracker - The name of the tracker to check
 * @param eventType - The event type to check
 * @returns true if the tracker should receive the event, false otherwise
 */
function shouldTrackEvent(tracker: string, eventType: TrackingEvents): boolean {
  const allowedEvents = TRACKER_EVENT_FILTERS[tracker]
  if (!allowedEvents) {
    return true
  }
  return allowedEvents.has(eventType)
}

/**
 * Extracts conversion data fields from a data object.
 *
 * @param data - The data object to extract conversion fields from
 * @returns ConversionData object with conversion fields, or undefined if data is not an object
 */
function extractConversionData(data?: object): ConversionData | undefined {
  if (!data || typeof data !== 'object') {
    return undefined
  }
  return {
    restaurantGuid: (data as any).restaurant_guid,
    conversionId: (data as any).conversion_id,
    amount: (data as any).amount,
    amountCurrencyCode: (data as any).amount_currency_code,
    context: (data as any).context
  }
}

/**
 * True on the default Toast ordering surface (order.toasttab.com/online),
 * where the guest-analytics-intake service rejects events. Deliberately narrow:
 * .toast.site domains, the egiftcard path, and tables.toasttab.com are still
 * tracked.
 */
function isGuestAnalyticsExcludedSurface(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const { hostname, pathname } = window.location
  return (
    hostname === resources.toastOrderHost &&
    (pathname === TOASTTAB_BOO_PATH ||
      pathname.startsWith(`${TOASTTAB_BOO_PATH}/`))
  )
}

const IDENTIFY_TYPE = 'identify' as const
const TRACK_TYPE = 'track' as const
export type QueueItem =
  | {
      type: typeof IDENTIFY_TYPE
      payload: amplitude.Identify
    }
  | {
      type: typeof TRACK_TYPE
      payload: {
        name: TrackingEvents
        data?: object
        skipAmplitude: boolean
      }
    }

interface AdsConsentQueueItem {
  conversionActionData: {
    cart: any
    cartGuid: string
    pixelsV2: Pixels
    adsManagerRestaurantConversionsData:
      | AdsManagerRestaurantConversionsData
      | null
      | undefined
  }
}

/**
 * Tracker class used to wrap tracking calls for third-party analytics platforms (Amplitude, Guest Analytics).
 * The module exports a singleton instance of the Tracker class.
 *
 * Consent Management:
 * `tracker.consentRevoked` is a boolean that is set to true if the user has revoked analytics consent. If consent is
 * revoked, tracking events are queued up in `tracker.pendingConsentQueue` and are processed only when consent is
 * granted via `tracker.setConsent(true)`. Note that `tracker.trackConversion` is not affected by this value, as consent for
 * gtag and fbq are handled elsewhere.
 *
 * Blacklisting:
 * When your app launches, you should immediately set the `tracker.blacklisted` property to `true` or `false` depending on
 * whether or not you want to track events for the current environment. This determination is yours to make. E.g. you
 * may want to set `tracker.blacklisted = true;` if the current runtime environment is a server-side render of a web app or the
 * requesting client is a bot. Blacklisted clients will not trigger tracking events. Note that unlike `tracker.consentRevoked`,
 * it is assumed that `tracker.blacklisted` will not change after initialization. Therefore events are not queued when
 * `tracker.blacklisted` is set to `true` - they are simply dropped.
 *
 */
export class Tracker {
  debug: boolean
  pendingConsentQueue: QueueItem[]
  flushInterval: number
  blacklisted: boolean
  consentRevoked: boolean
  flushTimer: ReturnType<typeof setInterval> | null
  adsConsent: boolean
  adsPendingConsentQueue: AdsConsentQueueItem[]
  guestAnalyticsClient: GuestAnalyticsClient | null
  guestAnalyticsConsent: boolean | null
  authStatusReady: boolean
  pendingAuthStatusQueue: QueueItem[]

  constructor(options?: TrackerOptions) {
    this.debug = false
    this.pendingConsentQueue = []
    this.flushInterval = 2500 // 2.5 seconds
    this.blacklisted = (options && options.blacklisted) || false
    this.consentRevoked = true
    this.flushTimer = null

    // TODO: - Consider pulling this out into a separate service that can be used to track conversions for all embedded tracking tools. ( Meta / Google Ads / future partners)
    this.adsConsent = false
    this.adsPendingConsentQueue = []

    this.guestAnalyticsClient = null
    this.guestAnalyticsConsent = null

    this.authStatusReady = false
    this.pendingAuthStatusQueue = []

    // Bind all methods to instance, or else reference in a lambda can be re-bound
    this.processPendingConsentQueue = this.processPendingConsentQueue.bind(this)

    // Public methods
    this.track = this.track.bind(this)
    this.trackGuestAnalytics = this.trackGuestAnalytics.bind(this)
    this.trackConversion = this.trackConversion.bind(this)
    this.register = this.register.bind(this)
    this.setConsent = this.setConsent.bind(this)
    this.setAdsConsent = this.setAdsConsent.bind(this)
    this.processAdsPendingConsentQueue =
      this.processAdsPendingConsentQueue.bind(this)
    this.initGuestAnalytics = this.initGuestAnalytics.bind(this)
    this.startFlushTimer = this.startFlushTimer.bind(this)
    this.logMessage = this.logMessage.bind(this)
    this.flush = this.flush.bind(this)
    this.unregister = this.unregister.bind(this)
    this.setUser = this.setUser.bind(this)
    this._trackAdManagerConversions = this._trackAdManagerConversions.bind(this)
    this.registerSessionEventProperties =
      this.registerSessionEventProperties.bind(this)
    this.unregisterSessionEventProperties =
      this.unregisterSessionEventProperties.bind(this)

    if (typeof window !== 'undefined') {
      amplitude.init(amplitudeApiKey, undefined, {
        identityStorage: 'localStorage',
        autocapture: {
          attribution: true,
          pageViews: false,
          sessions: false,
          fileDownloads: false,
          formInteractions: false,
          elementInteractions: false
        }
      })
      this.startFlushTimer()

      // Flush auth queue after 3s regardless, to avoid permanently blocking events
      // if CustomerContext never mounts or never fires its register effect.
      setTimeout(() => {
        if (!this.authStatusReady) {
          this.authStatusReady = true
          this.flushPendingAuthStatusQueue()
        }
      }, 3000)
    }
  }

  /**
   * Used internally for logging in debug environments
   */
  logMessage(message: string, ...data: any[]): void {
    if (this.debug) {
      console.log(message, ...data)
    }
  }

  /**
   * Initialize Guest Analytics tracking for the current site.
   * This should be called once per site/session with the site GUID.
   * Sets initial consent state and tracks SESSION_START event.
   *
   * @param siteGuid - The site GUID to track analytics for
   */
  initGuestAnalytics(siteGuid: string): void {
    if (
      typeof window === 'undefined' ||
      this.blacklisted ||
      !siteGuid ||
      !resources.guestAnalyticsApiEndpoint ||
      this.guestAnalyticsClient ||
      isGuestAnalyticsExcludedSurface()
    ) {
      return
    }

    try {
      this.guestAnalyticsClient = getGuestAnalyticsClient({
        apiEndpoint: resources.guestAnalyticsApiEndpoint,
        siteGuid,
        batchSize: 3
      })

      if (this.guestAnalyticsConsent !== null) {
        this.guestAnalyticsClient.setConsent(this.guestAnalyticsConsent)
      }

      if (getSessionManager().isNewSessionStart()) {
        this.guestAnalyticsClient.trackEvent(TrackingEvents.SESSION_START)
      }

      this.logMessage('Guest Analytics initialized for site:', siteGuid)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          module: 'guest-analytics',
          action: 'initGuestAnalytics'
        },
        extra: {
          siteGuid
        }
      })
      this.guestAnalyticsClient = null
    }
  }

  startFlushTimer(): void {
    if (!this.flushTimer) {
      this.flushTimer = setInterval(() => {
        this.flush()
      }, this.flushInterval)
    }
  }

  /**
   * Tracking function for recording analytics events across all platforms (Amplitude, Guest Analytics).
   * This function does not send tracking signals to the user's GTM, GA, or Meta pixels.
   *
   * Note: If consent is revoked, the event is queued up in `pendingConsentQueue` and is processed only when consent is
   * granted via `tracker.setConsent(true)`.
   *
   * @param name - The name of the event
   * @param data - Optional data object to be submitted with the event
   * @param skipAmplitude - Optional flag to skip tracking via Amplitude
   */
  track(name: TrackingEvents, data?: object, skipAmplitude = false): void {
    if (this.blacklisted) {
      this.logMessage('Tracker is blacklisted: Track event')
      return
    }

    if (!this.authStatusReady) {
      this.pendingAuthStatusQueue.push({
        type: 'track',
        payload: { name, data, skipAmplitude }
      })
      return
    }

    // Guest Analytics handles its own consent management
    if (this.guestAnalyticsClient && shouldTrackEvent('guestAnalytics', name)) {
      const conversionData = extractConversionData(data)
      this.guestAnalyticsClient.trackEvent(name, conversionData)
    }

    // event data takes precedence in case of collisions
    const enrichedData = {
      ...(typeof sessionStorage !== 'undefined'
        ? readSessionEventProperties()
        : {}),
      ...(data || {})
    }

    if (this.consentRevoked) {
      // Add a flag to the data object for tracking purposes, so that we can easily identify events that were
      // initially blocked due to lack of consent.
      const dataWithFlag = { ...enrichedData, consentDelayed: true }
      this.pendingConsentQueue.push({
        type: 'track',
        payload: { name, data: dataWithFlag, skipAmplitude }
      })
      this.logMessage('Tracker is pending consent: Track event queued')
    } else {
      if (typeof window !== 'undefined' && !skipAmplitude) {
        amplitude.track(name, enrichedData)
      }

      this.logMessage(`Tracking event: "${name}" with data: `, enrichedData)
    }
  }

  /**
   * Tracks events exclusively to Guest Analytics, bypassing Amplitude.
   * This method bypasses consent checks and is intended for first-party analytics only.
   *
   * @param {TrackingEvents} eventType - The event type to track (e.g., TrackingEvents.PAGE_VIEW).
   * @param {ConversionData} data - Optional conversion data (amount, currency_code, conversion_id).
   */
  trackGuestAnalytics(eventType: TrackingEvents, data?: ConversionData) {
    if (this.blacklisted) {
      this.logMessage('Tracker is blacklisted: Guest Analytics event')
      return
    }

    if (this.guestAnalyticsClient) {
      this.guestAnalyticsClient.trackEvent(eventType, data)
      this.logMessage(`Tracking Guest Analytics event: "${eventType}"`)
    }
  }

  /**
   * Flush analytics platforms to ensure pending events are sent.
   */
  flush(): void {
    if (typeof window === 'undefined') {
      return
    }
    amplitude.flush()
    if (this.guestAnalyticsClient) {
      this.guestAnalyticsClient.flush()
    }
  }

  /**
   * Registers a set of user properties - which are included with all subsequent events
   *
   * Note: If consent is revoked, the registration is queued up in `pendingConsentQueue` and is processed only when consent is
   * granted via `tracker.setConsent(true)`.
   *
   * Note: User properties may not be the best option depending on your tracking needs, as they can become "stale" and
   * carry over between web sessions and restaurant contexts within the application.
   *
   * @param data - Data object to be registered
   */
  register(data: object): void {
    if (typeof window === 'undefined' || this.blacklisted) {
      return
    }
    const identifyObj = new amplitude.Identify()
    Object.keys(data).forEach((key) => identifyObj.set(key, (data as any)[key]))

    if (this.consentRevoked) {
      this.pendingConsentQueue.push({
        type: 'identify',
        payload: identifyObj
      })
    } else {
      amplitude.identify(identifyObj)
    }

    // Signal that auth status has been determined (whether logged in or not)
    if ('authenticationStatus' in data && !this.authStatusReady) {
      this.authStatusReady = true
      this.flushPendingAuthStatusQueue()
    }
  }

  /**
   * Registers properties to be included on all subsequent Amplitude track() calls for the lifetime
   * of this Tracker instance (i.e. the current browser session). Unlike register(), these are sent
   * as event properties rather than user properties, so they do not persist across sessions.
   *
   * @param properties - Key/value pairs to merge into every outgoing event
   */
  registerSessionEventProperties(properties: Record<string, any>): void {
    if (typeof sessionStorage === 'undefined') return
    const existing = readSessionEventProperties()
    sessionStorage.setItem(
      SESSION_EVENT_PROPERTIES_KEY,
      JSON.stringify({ ...existing, ...properties })
    )
  }

  /**
   * Removes previously registered session event properties by key.
   *
   * @param keys - Array of property keys to remove from every outgoing event
   */
  unregisterSessionEventProperties(keys: string[]): void {
    if (typeof sessionStorage === 'undefined') return
    const existing = readSessionEventProperties()
    keys.forEach((key) => delete existing[key])
    sessionStorage.setItem(
      SESSION_EVENT_PROPERTIES_KEY,
      JSON.stringify(existing)
    )
  }

  /**
   * Sets the consent status for the tracker. If consent is granted, the events in `pendingConsentQueue` are processed.
   *
   * Note: consent does not affect `trackConversion()` calls, as consent for gtag and fbq are handled elsewhere.
   *
   * @param consent - Whether the user has granted consent
   */
  setConsent(consent: boolean): void {
    this.consentRevoked = !consent
    this.guestAnalyticsConsent = consent

    if (this.guestAnalyticsClient) {
      this.guestAnalyticsClient.setConsent(consent)
    }

    if (!this.blacklisted && !this.consentRevoked) {
      this.processPendingConsentQueue()
    }
  }

  processPendingConsentQueue(): void {
    if (this.pendingConsentQueue.length) {
      this.pendingConsentQueue.forEach((item) => {
        try {
          switch (item.type) {
            case IDENTIFY_TYPE:
              amplitude.identify(item.payload)
              break
            case TRACK_TYPE:
              if (!item.payload.skipAmplitude) {
                amplitude.track(item.payload.name, item.payload.data)
              }
              break
            default:
              console.error(
                'Unknown item type in pendingConsentQueue: ',
                (item as QueueItem).type
              )
              break
          }
        } catch (e) {
          console.error('Error processing item in pendingConsentQueue: ', e)
        }
      })
      this.pendingConsentQueue = []
    }
  }

  setAdsConsent(consent: boolean): void {
    this.adsConsent = consent
    if (this.adsConsent) {
      this.processAdsPendingConsentQueue()
    }
  }

  processAdsPendingConsentQueue(): void {
    if (this.adsPendingConsentQueue.length) {
      this.adsPendingConsentQueue.forEach((item) => {
        this._trackAdManagerConversions(
          item.conversionActionData.cart,
          item.conversionActionData.cartGuid,
          item.conversionActionData.pixelsV2,
          item.conversionActionData.adsManagerRestaurantConversionsData
        )
      })
      this.adsPendingConsentQueue = []
    }
  }

  /**
   * Unregister a set of properties which are then excluded with all subsequent events - only applicable to Amplitude.
   *
   * Note: If consent is revoked, the unregistration is queued up in `pendingConsentQueue` and is processed only when consent is
   * granted via `tracker.setConsent(true)`.
   *
   * @param keys - Array of user property keys to remove.
   */
  unregister(keys: string[]): void {
    if (typeof window === 'undefined' || this.blacklisted) {
      return
    }
    const identifyObj = new amplitude.Identify()
    keys.forEach((key) => identifyObj.unset(key))

    if (this.consentRevoked) {
      this.pendingConsentQueue.push({
        type: 'identify',
        payload: identifyObj
      })
    } else {
      amplitude.identify(identifyObj)
    }
  }

  /**
   * Tracking function specifically for conversion tracking. It calls the special conversion event for
   * all embedded tracking tools with the given dollar value.
   *
   * TODO ; Consider pulling this out into a separate service that can be used to track conversions for all embedded tracking tools. ( Meta / Google Ads / future partners)
   *
   * @param cart - The cart object associated with the order
   * @param cartGuid - The guid for the cart object associated with the order
   * @param pixelsV2 - The pixelsV2 object containing the conversion pixels
   * @param adsManagerRestaurantConversionsData - The adsManagerRestaurantConversionsData object containing the conversion pixels
   */
  trackConversion(
    cart: any,
    cartGuid: string,
    pixelsV2: Pixels,
    adsManagerRestaurantConversionsData:
      | AdsManagerRestaurantConversionsData
      | null
      | undefined
  ): void {
    try {
      // Extract restaurant identification data
      const restaurantGuid = cart.restaurant?.guid
      const restaurantName = cart.restaurant?.name
      const managementGuid = cart.restaurant?.managementSetGuid

      // Facebook tracking pixel
      if (
        typeof window.fbq !== 'undefined' &&
        cart.order?.preDiscountItemsSubtotal
      ) {
        window.fbq('track', 'Purchase', {
          currency: 'USD',
          value: cart.order.preDiscountItemsSubtotal,
          restaurant_guid: restaurantGuid,
          restaurant_name: restaurantName,
          management_guid: managementGuid
        })
      }

      // Google Analytics purchase event — use explicit send_to to avoid
      // broadcasting to incompatible destinations (e.g. AW) which can
      // interfere with GA4/GTM delivery when Toast Ads is active (WOO-5135)
      if (
        typeof window.gtag !== 'undefined' &&
        cart.order?.preDiscountItemsSubtotal
      ) {
        const sendTo = [
          pixelsV2?.google_analytics?.scriptArg,
          pixelsV2?.google_tag_manager?.scriptArg
        ].filter(Boolean)

        window.gtag('event', 'purchase', {
          transaction_id: cartGuid,
          value: cart.order.preDiscountItemsSubtotal,
          tax: cart.order.taxV2,
          currency: 'USD',
          coupon: cart.order.discounts?.restaurantDiscount?.promoCode,
          restaurant_guid: restaurantGuid,
          restaurant_name: restaurantName,
          management_guid: managementGuid,
          ...(sendTo.length > 0 && { send_to: sendTo })
        })
      }

      // Don't send the ads manager conversion signals if we don't have consent
      if (!this.adsConsent) {
        this.adsPendingConsentQueue.push({
          conversionActionData: {
            cart,
            cartGuid,
            pixelsV2,
            adsManagerRestaurantConversionsData
          }
        })
      } else {
        this._trackAdManagerConversions(
          cart,
          cartGuid,
          pixelsV2,
          adsManagerRestaurantConversionsData
        )
      }

      // OwG Redirect conversion Tracking
      // Theoretically this should only be called client-side and thus an existential check for localStorage should not be needed, but doesn't hurt...
      if (
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('rwg_token')
      ) {
        const partnerId = isToastLocalSurfaceRequest()
          ? owg.partnerId
          : owg.ooPartnerId
        fetch(owg.conversionHost, {
          method: 'POST',
          body: JSON.stringify({
            conversion_partner_id: partnerId,
            rwg_token: sessionStorage.getItem('rwg_token')
          })
        })
          .catch((err) => {
            console.error('Error submitting conversion to OwG', err)
          })
          .finally(() => {
            sessionStorage.removeItem('rwg_token')
          })
      }

      // Flush analytics immediately after conversion to ensure delivery
      this.flush()
    } catch (e) {
      const logData = {
        cartGuid,
        pixelsV2,
        adsManagerRestaurantConversionsData
      }
      reportError('Error in trackConversion()', e, logData)
    }
  }

  /**
   * Private method to track ads conversions.
   * This method is only called when ads consent is granted or when processing queued conversions.
   *
   * @param cart - The cart object associated with the order
   * @param cartGuid - The guid for the cart object associated with the order
   * @param pixelsV2 - The pixelsV2 object containing the conversion pixels
   * @param adsManagerRestaurantConversionsData - The adsManagerRestaurantConversionsData object containing the conversion pixels
   */
  _trackAdManagerConversions(
    cart: any,
    cartGuid: string,
    pixelsV2: Pixels,
    adsManagerRestaurantConversionsData:
      | AdsManagerRestaurantConversionsData
      | null
      | undefined
  ): void {
    // Extract restaurant identification data
    const restaurantGuid = cart.restaurant?.guid
    const restaurantName = cart.restaurant?.name
    const managementGuid = cart.restaurant?.managementSetGuid

    const conversions = pixelsV2?.google_ads_conversion_tag?.conversions
    if (
      typeof window.gtag !== 'undefined' &&
      conversions &&
      conversions.length > 0
    ) {
      conversions!.forEach((conversion) => {
        if (!conversion || typeof window.gtag === 'undefined') return
        window.gtag('event', conversion.type, {
          transaction_id: cartGuid,
          value: cart.order.preDiscountItemsSubtotal,
          tax: cart.order.taxV2,
          currency: 'USD',
          coupon: cart.order.discounts?.restaurantDiscount?.promoCode,
          send_to: `${pixelsV2?.google_ads_conversion_tag?.scriptArg}/${conversion.label}`,
          restaurant_guid: restaurantGuid,
          restaurant_name: restaurantName,
          management_guid: managementGuid
        })
      })
    }

    if (adsManagerRestaurantConversionsData) {
      const pixelId =
        adsManagerRestaurantConversionsData.metaConversionsData?.pixelId
      if (typeof window.fbq !== 'undefined' && pixelId) {
        window.fbq('trackSingle', pixelId, 'Purchase', {
          currency: 'USD',
          value: cart.order.preDiscountItemsSubtotal,
          source_type: 'online-ordering',
          action_source: 'website',
          partner_agent: 'Toast',
          restaurant_guid: restaurantGuid,
          restaurant_name: restaurantName,
          management_guid: managementGuid
        })
      }

      if (
        typeof window.gtag !== 'undefined' &&
        adsManagerRestaurantConversionsData.googleConversionsData
          ?.conversionLabels &&
        adsManagerRestaurantConversionsData.googleConversionsData
          .conversionLabels.length > 0
      ) {
        const googleConversionsData =
          adsManagerRestaurantConversionsData.googleConversionsData
        const conversionLabels = googleConversionsData?.conversionLabels
        if (conversionLabels) {
          conversionLabels!.forEach((conversion) => {
            if (!conversion || typeof window.gtag === 'undefined') return
            window.gtag('event', conversion.type, {
              transaction_id: cartGuid,
              value: cart.order.preDiscountItemsSubtotal,
              tax: cart.order.taxV2,
              currency: 'USD',
              coupon: cart.order.discounts?.restaurantDiscount?.promoCode,
              send_to: `${googleConversionsData.tagId}/${conversion.label}`,
              restaurant_guid: restaurantGuid,
              restaurant_name: restaurantName,
              management_guid: managementGuid
            })
          })
        }
      }
    }
  }

  private flushPendingAuthStatusQueue(): void {
    const queued = this.pendingAuthStatusQueue.splice(0)
    queued.forEach(({ payload }) => {
      if (payload && 'name' in payload) {
        this.track(payload.name, payload.data, payload.skipAmplitude)
      }
    })
  }

  /**
   * Sets the userId for amplitude events
   *
   * @param userId - The user's ID or undefined if not available
   */
  setUser(userId: string | undefined): void {
    amplitude.setUserId(userId)
  }
}

// Singleton instance used for tracking events on the client
export const tracker = new Tracker()

export default tracker
